import { useState } from "react";
import { GrDialog, GrDialogProps, GrTextField } from "@jnpmedi/grebob";
import { useQueryClient } from "@tanstack/react-query";
import { MvnMessage } from "@newLibs/utils";
import { UseBoolean } from "@newHooks/common/useBoolean";
import { notify } from "@newHooks/context/snackbar/notify";
import { useCreateFolderMutation } from "@newHooks/mutation/folder/createFolder.mutation";
import { FolderKey } from "@newHooks/query/folder/key";
import { InventoryKey } from "@newHooks/query/inventory/key";
import { DOCS_FOLDER_LANG } from "@maven-msg/lang/docs";
import { Organization } from "@maven-rest/auth/Organization.dto";
import { FolderCriteria, FolderType } from "@maven-rest/docs/Folder.dto";

interface AddFolderDialogProps {
  open: UseBoolean;
  folderId?: bigint;
  organization: Organization;
  folderType: FolderType;
  size?: GrDialogProps["size"];
}

export function AddFolderDialog({ open, folderId, organization, size = "xs", folderType }: AddFolderDialogProps) {
  const queryClient = useQueryClient();
  const [createFolderName, setCreateFolderName] = useState<string>("");

  const { mutate: createFolderRequest, isLoading: isCreateFolderLoading } = useCreateFolderMutation({
    onSuccess: () => {
      handleOnClose();
      if (folderType === FolderType.ARCHIVER) {
        queryClient.invalidateQueries({ queryKey: [InventoryKey.InventoryBalance] });
      }

      queryClient.invalidateQueries({ queryKey: [FolderKey.BrowseFolders] });
      notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_ARCHIVER_CREATE_SUCC), "SUCC");
    },
    onError: (error) => notify(MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_CREATE_FAIL, { message: error.responseMessage }), "ERROR"),
  });

  const handleFolderRequest = () => {
    if (isCreateFolderLoading) {
      return;
    }

    if (!createFolderName.trim().length) {
      notify(MvnMessage("Enter folder name."), "WARN");
      return;
    }

    createFolderRequest({
      organizationId: organization!.id,
      name: createFolderName,
      parentFolderId: folderId,
      folderType,
    });
  };

  const handleOnClose = () => {
    open.onFalse();
    setCreateFolderName("");
  };

  const handleTextFieldChange = (evt: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setCreateFolderName(evt.currentTarget.value);
  };

  const handleEnterKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleFolderRequest();
    }
  };

  return (
    <GrDialog
      title={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_ADD_DIALOG_HEADER)}
      open={open.value}
      size={size}
      confirmButton={{
        children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CREATE_NOUN),
        isLoading: isCreateFolderLoading,
        disabled: !createFolderName.trim().length,
      }}
      cancelButton={{ children: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_COMMON_CANCEL_NOUN) }}
      onClose={handleOnClose}
      onConfirm={handleFolderRequest}
    >
      <GrTextField
        fullWidth
        required
        autoFocus
        maxLength={FolderCriteria.FOLDER_NAME_MAXLEN}
        value={createFolderName}
        onChange={handleTextFieldChange}
        label={{ text: MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_ARCHIVER_NAME_LABEL) }}
        placeholder={MvnMessage(DOCS_FOLDER_LANG.DOCS_FOLDER_NAME_PLACEHOLDER)}
        onKeyDown={handleEnterKeyDown}
      />
    </GrDialog>
  );
}
